<template>
    <div class="insurance-chosen-variant"
        ref="variantRef">
        <div class="insurance-chosen-variant__process">
            <h3 class="insurance-chosen-variant__title">{{ headerHeading.title }}</h3>
            <p class="insurance-chosen-variant__subtitle">{{ headerHeading.subtitle }}</p>
            <FormClientDetails v-show="currentStep === steps[0] || currentStep === steps[1]"
                class="insurance-chosen-variant__form" 
                @inn-is-defined="currentStep = steps[1]"
                @prev-step="currentStep = steps[0]"
                @close="$emit('close')"
                @client-details-submit="onClientDetailsSubmit"
                />
            <FormPropertyDetails v-show="currentStep === steps[2]" 
                class="insurance-chosen-variant__form"
                :price="priceWithDiscount"
                @cancel-subscription="$emit('cancel-subscription')"
                @prev-step="currentStep = steps[1]"
                @coupon-discount="handleCouponDiscount"
                @form-submit="onPropertyDetailsSubmit"
            />
        </div>

        <InsuranceTariffCard v-if="tariffCard" 
            class="insurance-chosen-variant__card"
            ui-type="descriptional" 
            :tariff="tariffCard.tariff" 
            :title="tariffCard.title"
            :subtitle="tariffCard.subtitle" 
            :subtitlePrice="tariffCard.subtitlePrice"
            :tooltip-text="tariffCard.tooltipText"
            :mark="tariffCard?.mark"
            :old-price="tariffCard?.oldPrice" 
            :price="priceWithDiscount" 
            :list-items="tariffCard.listItems" />

        <ModalPolicyIssuanceError title="Произошла ошибка"
            :text="errorText"
            :visible="showErrorModal"
            modal-width="719px"
            @close="showErrorModal = false" />
    </div>
</template>

<script>
import InsuranceTariffCard from '@/components/ui/InsuranceTariffCard';
import FormClientDetails from '@/components/forms/FormClientDetails';
import FormPropertyDetails from '@/components/forms/FormPropertyDetails';
import ModalPolicyIssuanceError from '@/components/modals/ModalPolicyIssuanceError';
import { INSURANCE_CARDS } from '@/consts';
import { getNumbersFromString } from '@/utils/string-helpers';
import { payByAcquiring, payByLink } from '@/api/routes/payment';

export default {
    name: 'InsuranceChosenVariant',
    components: {
        InsuranceTariffCard,
        FormClientDetails,
        FormPropertyDetails,
        ModalPolicyIssuanceError
    },
    props: {
        tariff: {
            type: String,
            required: false
        },
    },
    inject: ['setShowGlobalSpinner'],
    data() {
        return {
            steps: ['define-inn', 'client-details', 'property-details'],
            currentStep: 'define-inn',
            showSuccessModal: false,
            showErrorModal: false,
            resultForm: {},
            discountPercent: 0,
            errorText: 'При оформлении оплаты произошла ошибка. Попробуйте позже.'
        }
    },
    methods: {
        onClientDetailsSubmit(form) {
            Object.assign(this.resultForm, form);
            this.currentStep = this.steps[2];
        },
        onPropertyDetailsSubmit(form, paymentType) {
            // paymentType: 'default-payment' | 'link-payment'
            Object.assign(this.resultForm, form, {tariff: this.tariff });

            this.processPayment(this.resultForm, paymentType);
        },
        handleCouponDiscount(discountPercent) {
            this.discountPercent = discountPercent;
        },
        async processPayment(form, paymentType) {
            form.summ_front = +this.priceWithDiscount;
            form.utm_source = !!this.$route.query?.utm_source ? this.$route.query.utm_source : '';
            form.utm_medium = !!this.$route.query?.utm_medium ? this.$route.query.utm_medium : '';
            form.utm_campaign = !!this.$route.query?.utm_campaign ? this.$route.query.utm_campaign : '';
            this.convertMaskedFields(form);
            this.setNullToInvalidFields(form);

            this.setShowGlobalSpinner(true);
            let response;

            // инициируем запрос на оплату
            switch(paymentType) {
                case 'default-payment':
                    response = await payByAcquiring(form);
                    response?.result && window.open(response.redirect, '_self');
                    break
                case 'link-payment':
                    response = await payByLink(form);
                    response?.result && this.$emit('payment-link-sent');
                    break
            }

            if (!response?.result) {
                // ошибка запроса на оплату
                this.showErrorModal = true;
                this.errorText = response.message;
            }

            // выключаем спиннер
            this.setShowGlobalSpinner(false);
        },

        convertMaskedFields(form) {
          form.phone_number = getNumbersFromString(form.phone_number);
          form.passport_number = getNumbersFromString(form.passport_number);
          form.area_object = +getNumbersFromString(form.area_object, true);
        },

        setNullToInvalidFields(form) {
            for (let key in form) {
                if (!form[key]) {
                    form[key] = null;
                }
            }
        },

        scrollToTop() {
            if (this.$refs?.variantRef) {
                this.$refs.variantRef.scrollIntoView({
                    behavior: 'smooth',
                    block: 'center',
                });
            }
        },
    },
    computed: {
        tariffCard() {
            return INSURANCE_CARDS.find(card => card.tariff === this.tariff)
        },
        priceWithDiscount() {
            if (this.tariffCard) {
                const discount = this.tariffCard.price - (this.tariffCard.price * this.discountPercent / 100);
                const discountToFixed = +discount.toFixed(2)
                return +discountToFixed > 0 ? discountToFixed.toString() : "0";
            } else return '0'
        },
        headerHeading() {
            switch (this.steps.indexOf(this.currentStep)) {
                case 0:
                    return { 
                        title: 'Укажите ИНН страхователя', 
                        subtitle: 'Введите ИНН и Мы автоматически заполним ваши данные' 
                    }
                case 1:
                    return {
                        title: 'Данные страхователя', 
                        subtitle: 'Проверьте заполненные данные и внесите информацию в пустые поля' 
                    }
                case 2:
                    return {
                        title: 'Данные страхуемого имущества', 
                        subtitle: 'Внесите информацию в пустые поля' 
                    }
            }
        }
    },
    watch: {
        currentStep() {
            this.scrollToTop()
        }
    }
}
</script>

<style scoped lang="scss">
.insurance-chosen-variant {
    display: flex;

    @media #{$screen-medium} {
        flex-direction: column;
    }

    &__process {
        flex-grow: 1;
    }

    &__title {
        @include text-large($font-weight-semibold);

        @media #{$screen-big} {
            font-size: rem($font-size-medium);
        }
    }

    &__subtitle {
        @include text-medium($font-weight-regular, $line-height-medium);

        font-family: $font-family-secondary;

        margin-top: rem($gap-normal);
        margin-bottom: rem($gap-medium);
        width: fit-content;

        color: $color-text-muted-low2;

        @media #{$screen-big} {
            font-size: rem($font-size-normal);
        }
    }

    &__form {

    }

    &__card {
        width: 46%;
        margin-left: rem($gap-large);

        @media #{$screen-medium} {
            width: 100%;

            margin-left: 0;
            margin-top: rem($gap-big);
        }
    }
}
</style>